/**
 * In-chat document workspace. Sections are a temporary draft built from the
 * uploaded file's extracted text. The uploaded original is never changed:
 * "Use in assistant" only replaces the working text the assistant reads.
 */

let seq = 0;
const sectionId = () => {
  seq += 1;
  return `sec-${Date.now().toString(36)}-${seq}`;
};

const HEADING = /^(#{1,6}\s+.+|slide\s+\d+\b.*|chapter\s+\d+\b.*|section\s+\d+\b.*|\d+(\.\d+)*[.)]\s+[A-Z].{0,80}|[A-Z][A-Z0-9 ,:&/()-]{3,80})$/;

/** Split extracted text into editable sections on headings, else by paragraph groups. */
export function textToSections(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const sections = [];
  let current = null;
  const push = () => {
    if (current && (current.heading || current.body.trim())) {
      sections.push({ ...current, body: current.body.trim() });
    }
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed && trimmed.length <= 90 && HEADING.test(trimmed)) {
      push();
      current = { id: sectionId(), heading: trimmed.replace(/^#{1,6}\s+/, ""), body: "" };
    } else {
      if (!current) current = { id: sectionId(), heading: "", body: "" };
      current.body += `${line}\n`;
    }
  });
  push();

  if (sections.length <= 1) {
    const paragraphs = String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    const groups = [];
    for (let i = 0; i < paragraphs.length; i += 3) groups.push(paragraphs.slice(i, i + 3).join("\n\n"));
    return groups.slice(0, 60).map((body, i) => ({ id: sectionId(), heading: `Part ${i + 1}`, body }));
  }
  return sections.slice(0, 80);
}

export function sectionsToText(sections) {
  return (sections || [])
    .map((s) => [s.heading ? `## ${s.heading}` : "", s.body].filter(Boolean).join("\n"))
    .join("\n\n")
    .trim();
}

export function createDocumentDraft(source, text) {
  return {
    source_id: source.id,
    file_name: source.file_name || source.name || "Document",
    sections: textToSections(text),
    dirty: false,
    applied_at: null,
  };
}

const touch = (doc, sections) => ({ ...doc, sections, dirty: true });

export function updateSection(doc, id, patch) {
  return touch(
    doc,
    doc.sections.map((s) =>
      s.id === id
        ? {
            ...s,
            heading: patch.heading !== undefined ? String(patch.heading) : s.heading,
            body: patch.body !== undefined ? String(patch.body) : s.body,
          }
        : s
    )
  );
}

export function removeSection(doc, id) {
  return touch(doc, doc.sections.filter((s) => s.id !== id));
}

export function addSection(doc, afterId = null) {
  const fresh = { id: sectionId(), heading: "New section", body: "" };
  const sections = [...doc.sections];
  const at = afterId ? sections.findIndex((s) => s.id === afterId) + 1 : sections.length;
  sections.splice(at, 0, fresh);
  return touch(doc, sections);
}

export function moveSection(doc, id, delta) {
  const sections = [...doc.sections];
  const from = sections.findIndex((s) => s.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= sections.length) return doc;
  const [item] = sections.splice(from, 1);
  sections.splice(to, 0, item);
  return touch(doc, sections);
}
